import { useEffect, useLayoutEffect } from 'react';
import type { PageContent } from '../types';
import { defaultPageContent } from '../contexts/DefaultPageContent';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const updateMetaTag = (attr: 'name' | 'property', value: string, content: string) => {
  if (!content) return;
  const selector = `meta[${attr}="${value}"]`;
  let element = document.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, value);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

export const updateLinkTag = (rel: string, href: string) => {
  if (!href) return;
  let element = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

export const updateRobotsTag = (noindex: boolean) => {
  const ROBOTS_TAG_SELECTOR = 'meta[name="robots"]';
  let element = document.querySelector<HTMLMetaElement>(ROBOTS_TAG_SELECTOR);

  if (noindex) {
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute('name', 'robots');
      document.head.appendChild(element);
    }
    element.setAttribute('content', 'noindex');
  } else if (element) {
    element.remove();
  }
};

const CUSTOM_HEAD_ATTRIBUTE = 'data-custom-head-node';

export const updateCustomHead = (htmlString: string) => {
  // 1. Remove any previously added custom head nodes
  document.querySelectorAll(`[${CUSTOM_HEAD_ATTRIBUTE}]`).forEach(el => el.remove());

  // 2. Add new nodes if htmlString is provided
  if (htmlString && htmlString.trim()) {
    const template = document.createElement('template');
    template.innerHTML = htmlString.trim();

    // 3. Mark each new node before appending it
    const nodesToAppend = Array.from(template.content.childNodes);
    nodesToAppend.forEach(node => {
      // Only add attributes to element nodes
      if (node.nodeType === Node.ELEMENT_NODE) {
        (node as Element).setAttribute(CUSTOM_HEAD_ATTRIBUTE, 'true');
      }
    });

    // 4. Append the new, marked nodes to the head
    document.head.append(...nodesToAppend);
  }
};

export const useSeo = (pageContent: PageContent | undefined, pageSlug: string = 'home', onSeoApplied?: () => void) => {
  useIsomorphicLayoutEffect(() => {
    if (!pageContent) {
      return;
    }

    const fallbackSeo = (defaultPageContent[pageSlug as keyof typeof defaultPageContent] || defaultPageContent.home).seo;
    const currentSeo = pageContent?.seo;

    if (!fallbackSeo) return;

    // Helper to determine if the page is a dynamically added one
    const isDynamicPage = !(pageSlug in defaultPageContent);
    
    const finalSlug = currentSeo?.slug?.trim() ? currentSeo.slug : fallbackSeo.slug;
    const computedFallbackCanonical = isDynamicPage 
        ? `https://www.brandgenius.app${finalSlug.startsWith('/') ? finalSlug : `/${finalSlug}`}`
        : fallbackSeo.canonical;

    // Combine current and fallback SEO data. Crucially, if a value from the database (currentSeo)
    // is an empty string, it will now correctly use the fallback value instead.
    const finalSeo = {
        title: currentSeo?.title?.trim() ? currentSeo.title : fallbackSeo.title,
        description: currentSeo?.description?.trim() ? currentSeo.description : fallbackSeo.description,
        canonical: currentSeo?.canonical?.trim() ? currentSeo.canonical : computedFallbackCanonical,
        slug: finalSlug,
        customHead: currentSeo?.customHead?.trim() ? currentSeo.customHead : fallbackSeo.customHead,
        noindex: currentSeo?.noindex ?? fallbackSeo.noindex,
        ogImageUrl: (currentSeo?.ogImageUrl?.trim() ? currentSeo.ogImageUrl : fallbackSeo.ogImageUrl) || '/og-default.jpg',
    };
    
    // Helper to normalize a URL for crawlers, ensuring it's absolute and has a consistent trailing slash.
    const normalizeUrlForCrawler = (pathOrUrl: string): string => {
      try {
        const absoluteUrl = pathOrUrl.startsWith('http')
          ? pathOrUrl
          : `${window.location.origin}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
        
        const url = new URL(absoluteUrl);
        // A path is a directory if it's not the root and doesn't have a file extension.
        const hasFileExtension = /\.[^/.]+$/.test(url.pathname);
        if (url.pathname.length > 1 && !url.pathname.endsWith('/') && !hasFileExtension) {
          url.pathname += '/';
        }
        return url.toString();
      } catch (e) {
        console.error("Could not parse or normalize URL:", pathOrUrl, e);
        return pathOrUrl.startsWith('http') ? pathOrUrl : `${window.location.origin}${pathOrUrl}`;
      }
    };
        
    const absoluteOgImageUrl = finalSeo.ogImageUrl.startsWith('http') 
        ? finalSeo.ogImageUrl 
        : `${window.location.origin}${finalSeo.ogImageUrl.startsWith('/') ? finalSeo.ogImageUrl : `/${finalSeo.ogImageUrl}`}`;

    const finalCanonicalUrl = normalizeUrlForCrawler(finalSeo.canonical);

    // --- Update DOM ---
    document.title = finalSeo.title;
    updateMetaTag('name', 'description', finalSeo.description);
    updateLinkTag('canonical', finalCanonicalUrl);
    updateRobotsTag(finalSeo.noindex);
    updateCustomHead(finalSeo.customHead);

    // --- Open Graph & Twitter ---
    updateMetaTag('property', 'og:title', finalSeo.title);
    updateMetaTag('property', 'og:description', finalSeo.description);
    updateMetaTag('property', 'og:url', finalCanonicalUrl); // Use normalized URL
    updateMetaTag('property', 'og:image', absoluteOgImageUrl);
    updateMetaTag('property', 'og:type', 'website');
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', finalSeo.title);
    updateMetaTag('name', 'twitter:description', finalSeo.description);
    updateMetaTag('name', 'twitter:image', absoluteOgImageUrl);
    
    // After all DOM manipulations are complete, execute the callback
    // to signal that the page is ready for pre-rendering.
    onSeoApplied?.();

  }, [pageContent, pageSlug, onSeoApplied]);

  // The hook no longer needs to return anything.
};